/* wb-menu-obfuscation — N2, activation native uniquement à l'interaction. */
(() => {
  if (window.wbMenuObfuscationInstalled) return;
  window.wbMenuObfuscationInstalled = true;
  const selector = '#MainNavigation a[data-wb-menu-url], #MenuDrawer a[data-wb-menu-url]';
  const active = new Set();
  const find = (event) => event.target.closest?.(selector);
  const clear = (link) => {
    link.removeAttribute('href');
    active.delete(link);
  };
  const activate = (link) => {
    try {
      const bytes = Uint8Array.from(atob(link.dataset.wbMenuUrl), (c) => c.charCodeAt(0));
      const value = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      const url = new URL(value, document.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) return false;
      link.setAttribute('href', value);
      active.add(link);
      return true;
    } catch {
      return false;
    }
  };

  // Ne rien décoder au chargement, au survol, au focus ou à l'ouverture du menu.
  // pointerdown prépare aussi le comportement natif du bouton central.
  for (const type of ['pointerdown', 'contextmenu', 'click', 'auxclick']) {
    document.addEventListener(type, (event) => {
      const link = find(event);
      if (!link) return;
      if (!activate(link)) {
        event.preventDefault();
        return;
      }
      // L'action par défaut conserve Ctrl/Cmd/Shift et le bouton central.
      // Le menu contextuel garde son href jusqu'à la sortie/focusout.
      if (type === 'click' || (type === 'auxclick' && event.button === 1)) {
        setTimeout(() => clear(link), 0);
      }
    }, true);
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') active.forEach(clear);
    const link = find(event);
    if (!link || event.key !== 'Enter' || event.repeat) return;
    event.preventDefault();
    if (!activate(link)) return;
    link.dispatchEvent(new MouseEvent('click', {
      bubbles: true, cancelable: true, view: window,
      ctrlKey: event.ctrlKey, metaKey: event.metaKey,
      shiftKey: event.shiftKey, altKey: event.altKey,
    }));
    setTimeout(() => clear(link), 0);
  }, true);
  for (const type of ['pointerout', 'pointercancel', 'focusout']) {
    document.addEventListener(type, (event) => {
      const link = find(event);
      if (link && !link.contains(event.relatedTarget)) clear(link);
    }, true);
  }
  window.addEventListener('pagehide', () => active.forEach(clear));
})();

class BasicHeader extends HTMLElement {
  constructor() {
    super();
    this._heightFrame = 0;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  get headerNavigation() {
    return this.querySelector('.header__bottom');
  }

  get navigationToggleButton() {
    return this.querySelector('.toggle-navigation-button');
  }

  get enableTransparent() {
    return this.dataset.enableTransparent === 'true';
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    // window.addEventListener('resize', this.setMenuHeight.bind(this));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent');
    }
  }

  calculateHeaderGroupHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;

    headerGroups.forEach((section) => {
      totalHeight += section.offsetHeight;
    });

    return totalHeight;
  }

  setHeight() {
    if (this._heightFrame) return;
    this._heightFrame = requestAnimationFrame(() => {
      this._heightFrame = 0;
      const offsetHeight = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.parentElement.offsetTop);
      const offsetNavigationHeight = Math.round(this.headerNavigation.offsetHeight);
      const headerGroupHeight = this.calculateHeaderGroupHeight();

      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--header-navigation-height', `${offsetNavigationHeight - 1}px`);
      document.documentElement.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    });
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    // Group CSS classes in one object for easier management
    this.classes = {
      pinned: 'header-pinned',
      headerScrolled: 'header-scrolled',
      show: 'is-show',
      hide: 'is-hide',
      headerSticky: 'header-sticky',
      isHideNav: 'is-hide-nav',
    };

    // Track navigation toggle state
    this.navigationManuallyToggled = false;

    // Initialize scroll tracking variables
    this.currentScrollTop = 0;
    this.lastScrollPos = 0;

    // Add scroll threshold to prevent quick unpinning
    this.scrollThreshold = 200; // Minimum scroll amount before unpinning
    this.scrollDirection = 'none';
    this.scrollDistance = 0;
    this._scrollFrame = 0;
    this._pendingScrollTop = 0;
  }

  // Getters for easier property access
  get isAlwaysSticky() {
    return this.dataset.stickyType === 'always';
  }

  get collapseOnScroll() {
    return this.dataset.collapseOnScroll === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    // Store initial scroll position
    this.firstScrollTop = window.scrollY;

    // Cache header dimensions for performance optimization
    this.headerBounds = this.headerSection.getBoundingClientRect();

    // Initialize sticky header
    this.initStickyHeader();

    // Register toggle button event if needed
    if (this.collapseOnScroll && this.navigationToggleButton) {
      this.navigationToggleButton.addEventListener('click', this.handleNavigationToggle.bind(this));
    }
  }

  // Initialize sticky header behavior
  initStickyHeader() {
    this.headerSection.classList.add(this.classes.headerSticky);
    this.headerSection.dataset.stickyType = this.dataset.stickyType;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }

  // Handle navigation toggle button click
  handleNavigationToggle(event) {
    event.preventDefault();

    this.navigationManuallyToggled = true;
    const isHidden = this.headerNavigation.classList.contains(this.classes.hide);

    // Toggle visibility state
    setTimeout(
      () => {
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
      },
      isHidden ? 0 : 250
    );
    this.headerNavigation.classList.toggle(this.classes.hide, !isHidden);
    this.headerNavigation.classList.toggle(this.classes.show, isHidden);
    this.navigationToggleButton.setAttribute('aria-expanded', isHidden);

    // Reset toggle state after delay
    setTimeout(() => {
      this.navigationManuallyToggled = false;
    }, 1000);
  }

  // Handle scroll events
  handleScroll() {
    const scrollTop = window.scrollY;
    this._pendingScrollTop = scrollTop;
    if (this._scrollFrame) return;
    this._scrollFrame = requestAnimationFrame(() => {
      this._scrollFrame = 0;
      const nextScrollTop = this._pendingScrollTop;
      const headerBoundsTop = this.headerBounds.top + this.firstScrollTop;
      const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop;
      this.updateScrollMetrics(nextScrollTop);
      const isScrolledPastHeader = nextScrollTop > headerBoundsTop;

      // Handle different scroll positions
      if (isScrolledPastHeader) {
        this.handleScrolledPastHeader(nextScrollTop, headerBoundsBottom);
      } else {
        this.handleScrolledBeforeHeader();
      }

      this.currentScrollTop = nextScrollTop;
    });
  }

  // Update scroll direction and accumulated distance
  updateScrollMetrics(scrollTop) {
    // Determine scroll direction
    const newDirection = scrollTop > this.currentScrollTop ? 'down' : 'up';

    // Reset accumulated distance when direction changes
    if (newDirection !== this.scrollDirection) {
      this.scrollDistance = 0;
      this.scrollDirection = newDirection;
    } else {
      // Accumulate scroll distance in the same direction
      this.scrollDistance += Math.abs(scrollTop - this.currentScrollTop);
    }
  }

  // Handle scroll position after passing the header
  handleScrolledPastHeader(scrollTop, headerBoundsBottom) {
    this.headerSection.classList.add(this.classes.headerScrolled);

    // Handle collapse on scroll behavior
    if (this.collapseOnScroll) {
      this.navigationToggleButton.classList.add(this.classes.show);
      if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(this.classes.show)) {
        this.headerNavigation.classList.add(this.classes.hide);
        document.body.classList.add(this.classes.isHideNav);
      }
    }

    // Handle sticky behavior based on configuration
    if (this.isAlwaysSticky) {
      document.body.classList.add(this.classes.pinned);
    } else {
      // Improved sticky on scroll up logic
      const isScrollingUp = this.scrollDirection === 'up';
      const isNearHeader = scrollTop < headerBoundsBottom + 100;
      const hasScrolledEnough = this.scrollDistance >= this.scrollThreshold;

      if (isScrollingUp || isNearHeader) {
        document.body.classList.add(this.classes.pinned);
      } else if (!this.navigationManuallyToggled && hasScrolledEnough) {
        // Only unpin when scrolled down enough and not manually toggled
        document.body.classList.remove(this.classes.pinned);
      }
    }
  }

  // Handle scroll position before the header
  handleScrolledBeforeHeader() {
    this.headerSection.classList.remove(this.classes.headerScrolled);

    if (this.collapseOnScroll) {
      if (!this.navigationManuallyToggled) {
        document.body.classList.remove(this.classes.isHideNav);
        this.headerNavigation.classList.remove(this.classes.hide);
        this.headerNavigation.classList.remove(this.classes.show);
        this.navigationToggleButton.setAttribute('aria-expanded', false);
      }
      this.navigationToggleButton.classList.remove(this.classes.show);
    }

    if (this.isAlwaysSticky) {
      document.body.classList.remove(this.classes.pinned);
    }
  }
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' });

const clearDropdownCount = new WeakMap();

class NavDropdown extends HTMLElement {
  constructor() {
    super();
    this.classes = { bodyClass: 'has-dropdown-menu' };
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    };

    this.triggerButton = this.querySelector(':scope > button[aria-expanded]');
    this.contentElement = this.querySelector(':scope > [data-nav-panel]');

    this._open = false;

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);

    this.hoverEnterTimer = null;
    this.hoverLeaveTimer = null;

    if (this.triggerButton) {
      this.triggerButton.addEventListener('click', this.handleTriggerClick.bind(this));
    }
  }

  connectedCallback() {
    if (!FoxTheme.config.isTouch && this.trigger === 'hover') {
      this.addEventListener('mouseenter', () => {
        if (this.hoverLeaveTimer) {
          clearTimeout(this.hoverLeaveTimer);
          this.hoverLeaveTimer = null;
        }
        this.hoverEnterTimer = setTimeout(() => {
          this.detectHover({ type: 'mouseenter' });
        }, 100);
      });

      this.addEventListener('mouseleave', () => {
        if (this.hoverEnterTimer) {
          clearTimeout(this.hoverEnterTimer);
          this.hoverEnterTimer = null;
        }
        this.hoverLeaveTimer = setTimeout(() => {
          this.detectHover({ type: 'mouseleave' });
        }, 150);
      });

    }

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.open = false;
      }
    });
  }

  get trigger() {
    if (FoxTheme.config.isTouch) return 'click';
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    return this.getAttribute('level') || 'top';
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;
      if (this.isConnected) {
        this.transition(value);
      } else {
        this.toggleAttribute('data-open', value);
      }
    }
  }

  get open() {
    return this._open;
  }

  async transition(value) {
    if (value) {
      clearDropdownCount.set(NavDropdown, clearDropdownCount.get(NavDropdown) + 1);

      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('data-open', '');
      if (this.contentElement) this.contentElement.removeAttribute('inert');
      if (this.triggerButton) this.triggerButton.setAttribute('aria-expanded', 'true');

      setTimeout(() => {
        if (this.contentElement) this.contentElement.setAttribute('data-open', '');
      }, 100);

      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);

      await this.showWithTransition();

      const menuProductList = this.querySelector('menu-product-list');
      if (menuProductList) menuProductList.calcNavButtonsPosition();

      this.needsReverse();

      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    } else {
      clearDropdownCount.set(NavDropdown, clearDropdownCount.get(NavDropdown) - 1);

      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(NavDropdown) > 0);

      this.removeAttribute('data-open');
      if (this.contentElement) {
        this.contentElement.removeAttribute('data-open');
        this.contentElement.setAttribute('inert', '');
      }
      if (this.triggerButton) this.triggerButton.setAttribute('aria-expanded', 'false');

      document.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscKeyPress);

      await this.hideWithTransition();

      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  async showWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  async hideWithTransition() {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished;
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnNavDropdown = event.target.closest('nav-dropdown, nav-dropdown-mega') instanceof NavDropdown;

    if (!isClickInside && !isClickOnNavDropdown) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code === 'Escape') {
      const navDropdown = event.target.closest('nav-dropdown[data-open], nav-dropdown-mega[data-open]');
      if (navDropdown) {
        const btn = navDropdown.querySelector('button[aria-expanded]');
        if (btn) btn.focus();
        navDropdown.open = false;
      }
    }
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger === 'hover') {
      this.open = event.type === 'mouseenter';
    }
  }

  needsReverse() {
    if (!this.contentElement) return;
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse');
    }
  }
}

customElements.define('nav-dropdown', NavDropdown);
clearDropdownCount.set(NavDropdown, 0);

class NavDropdownMega extends NavDropdown {
  constructor() {
    super();
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }

  async hideWithTransition() {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished;
  }
}

customElements.define('nav-dropdown-mega', NavDropdownMega);

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

class MenuDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get header() {
    return this.closest('header');
  }

  // get menuItems() {
  //   if (!this._menuItems) {
  //     this._menuItems = this.querySelectorAll(
  //       '.menu-drawer__menus:not(.active) > ul > li, .menu-drawer__menus:not(.active) > .menu-drawer__footer'
  //     );
  //   }
  //   return this._menuItems;
  // }

  get requiresBodyAppended() {
    return false;
  }

  prepareToShow() {
    // INP mobile (26/10/2026) : la position du header se lit AVANT toute insertion — lue
    // après, elle forçait le calcul de mise en page des ~3 100 nœuds du menu dans le
    // toucher (p75 630 ms sur Android). Le contenu se prépare après la prochaine
    // peinture : le rAF tombe avant elle, le setTimeout après. Le tiroir ne glisse
    // que 100 ms plus tard (handleShowTransition) : il est rempli avant.
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    super.prepareToShow();
    if (!this.contenuPrepare) {
      requestAnimationFrame(() => setTimeout(() => this.preparerContenu()));
    }

    document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
  }

  preparerContenu() {
    if (this.contenuPrepare) return;
    this.contenuPrepare = true;
    // Webperf : le contenu du drawer est livré dans un <template> inerte
    // (voir snippets/menu-drawer.liquid). On l'injecte au premier show().
    const template = this.querySelector('template[data-menu-drawer-content]');
    if (template) {
      template.replaceWith(template.content);
    }
  }

  // animateMenuItems() {
  //   FoxTheme.Motion.animate(
  //     this.menuItems,
  //     {
  //       transform: ['translateX(-20px)', 'translateX(0)'],
  //       opacity: [0, 1],
  //     },
  //     {
  //       duration: 0.6,
  //       easing: [0.075, 0.82, 0.165, 1],
  //       delay: FoxTheme.Motion.stagger(0.1),
  //     }
  //   ).finished.then(() => {
  //     this.menuItems.forEach((item) => item.removeAttribute('style'));
  //   });
  // }

  handleAfterHide() {
    super.handleAfterHide();
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick();
      });
    });
  }
}
customElements.define('menu-drawer', MenuDrawer);

class MenuProductList extends HTMLElement {
  constructor() {
    super();
    this.initSlide();
    this.elementToFocus = this.querySelector('button');
  }

  get container() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get numberOfColumns() {
    return parseInt(this.dataset.columns);
  }

  initSlide() {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    });
    slider.init();

    const focusableElements = FoxTheme.a11y.getFocusableElements(this);

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide');

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide));
      });
    });
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;

    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }
}
customElements.define('menu-product-list', MenuProductList);

class MenuSidebar extends HTMLElement {
  constructor() {
    super();
    this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }

  get toggleHeaders() {
    return this.querySelectorAll('.menu-sidebar__toggle');
  }

  connectedCallback() {
    /* WB webperf 24/09/2026 — la hauteur n'est plus mesurée ici. Ce composant se
       connecte pendant l'analyse du header, menu FERMÉ (et absent sous 1024 px) :
       `offsetHeight` y valait 0 et forçait un calcul de mise en page complet
       (139 ms de reflow forcé, PageSpeed mobile de la PLP). Elle est prise par
       l'IntersectionObserver ci-dessous, quand le menu devient visible : la
       mise en page est alors à jour, et la valeur est la vraie. */
    const firstToggle = this.toggleHeaders[0];
    if (firstToggle) {
      this.setActiveItem(firstToggle, false);
    }

    this.toggleHeaders.forEach((toggle) => {
      toggle.addEventListener('mouseenter', this.handleSidenavMenuToggle);
      const btn = toggle.querySelector('button[aria-expanded]');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.setActiveItem(toggle);
        });
      }
    });

    this.setupIntersectionObserver();
  }

  setInitialMinHeight() {
    const container = this.closest('.mega-menu__wrapper');
    if (container) {
      const parentHeight = container.parentElement.offsetHeight;
      container.style.setProperty('--menu-sidebar-height', `${parentHeight}px`);
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.setInitialMinHeight();
          this.updateHeight();
        }
      });
    });

    observer.observe(this);
  }

  updateHeight() {
    const activeToggle = this.querySelector('.menu-sidebar__toggle.is-visible');
    if (activeToggle) {
      const container = this.closest('.mega-menu__wrapper');
      const content = activeToggle.nextElementSibling;
      if (container && content) {
        container.style.setProperty('--sidebar-height', `${content.scrollHeight}px`);
      }
    }
  }

  setActiveItem(toggleHeader, updateHeight = true) {
    const lastActive = this.querySelector('.menu-sidebar__toggle.is-visible');
    if (lastActive) {
      lastActive.classList.remove('is-visible');
      const lastBtn = lastActive.querySelector('button[aria-expanded]');
      if (lastBtn) lastBtn.setAttribute('aria-expanded', 'false');
      const lastContent = lastActive.nextElementSibling;
      if (lastContent) lastContent.hidden = true;
    }

    toggleHeader.classList.add('is-visible');
    const btn = toggleHeader.querySelector('button[aria-expanded]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    const content = toggleHeader.nextElementSibling;
    if (content) content.hidden = false;

    if (updateHeight) {
      this.updateHeight();
    }
  }

  handleSidenavMenuToggle(evt) {
    const toggleHeader = evt.target.closest('.menu-sidebar__toggle');
    if (toggleHeader) {
      this.setActiveItem(toggleHeader);
    }
  }

  disconnectedCallback() {
    this.toggleHeaders.forEach((toggle) => {
      toggle.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
    });
  }
}

customElements.define('menu-sidebar', MenuSidebar);

/* wb-menu-stories — visuels posés à la demande, et player du rail.

   Le panneau du mega-menu reste dans le DOM avec `visibility: hidden` sur
   `.mega-menu__container`, ce qui n'empêche PAS le navigateur de télécharger un
   `loading="lazy"` : l'élément a une boîte et intersecte le viewport. C'est la
   raison pour laquelle les vignettes de collection du mega-menu sont commentées
   dans snippets/mega-menu.liquid depuis la passe webperf du 04/08/2026.

   Les visuels des stories sont donc émis sans `src`. Ils sont posés panneau par
   panneau : à l'ouverture du menu pour l'univers déjà actif, puis au survol ou
   au focus de chaque univers. Chargement de page = zéro image de story.

   Le défilement ne coûte rien tant que le menu est fermé : aucun `setInterval`,
   la progression est une animation CSS que seule la classe `is-live` déclenche,
   posée à l'ouverture d'un panneau et retirée à sa fermeture. L'avance se fait
   sur `animationend`, donc le survol qui met l'animation en pause suspend aussi
   le défilement — on peut lire une story sans qu'elle se dérobe. */
(() => {
  if (window.wbMenuStoriesInstalled) return;
  window.wbMenuStoriesInstalled = true;

  const done = new WeakSet();

  const poser = (img, src, srcset) => {
    if (srcset) img.srcset = srcset;
    img.src = src;
  };

  // Fenêtre de diffusion. Le serveur écarte déjà les stories hors de leurs
  // dates (select.liquid), mais une page que Shopify sert depuis son cache a pu
  // être calculée la veille : une story échue depuis y figure encore. Elle est
  // retirée ici au jour près, à la première ouverture du panneau et AVANT que
  // ses visuels ne se chargent. Jour de Paris, quel que soit le fuseau du
  // visiteur : c'est celui du marchand qui a saisi la date.
  // Une date de DÉBUT ne se rattrape pas ici : une story à venir n'est pas
  // dans le HTML, elle paraît au prochain calcul de la page.
  const jourParis = () => {
    const parts = {};
    const format = new Intl.DateTimeFormat('en', {
      timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    for (const {type, value} of format.formatToParts(new Date())) parts[type] = value;
    return Number(parts.year + parts.month + parts.day);
  };

  const purger = (panel) => {
    const rail = panel.querySelector('.wb-stories');
    if (!rail) return;
    const jour = jourParis();
    const echue = (el) => Boolean(el && el.dataset.wbFin) && Number(el.dataset.wbFin) < jour;
    const lignes = [...rail.querySelectorAll('.wb-story-row')];
    const vivantes = lignes.filter((ligne) => !echue(ligne));
    // Story unique : pas de file, sa date est portée par le visuel de la carte.
    const seuleEchue = !lignes.length && echue(rail.querySelector('.wb-story__media'));
    if (seuleEchue || (lignes.length && !vivantes.length)) {
      rail.remove();
      panel.removeAttribute('data-wb-stories');
      return;
    }
    if (vivantes.length === lignes.length) return;
    for (const ligne of lignes) if (echue(ligne)) ligne.remove();
    // Les barres sont identiques : en garder autant que de stories vivantes.
    [...rail.querySelectorAll('.wb-story__bar')].slice(vivantes.length)
      .forEach((barre) => barre.remove());
    vivantes.forEach((ligne, j) => { ligne.dataset.wbGo = j; });
    activer(rail, 0);
    if (vivantes.length < 2) {
      rail.querySelector('.wb-story__bars')?.remove();
      rail.querySelector('.wb-stories__next')?.remove();
    }
  };

  const reveal = (panel) => {
    if (!panel || done.has(panel)) return;
    done.add(panel);
    purger(panel);
    for (const img of panel.querySelectorAll('img[data-wbs-src]')) {
      poser(img, img.dataset.wbsSrc, img.dataset.wbsSrcset);
      img.removeAttribute('data-wbs-src');
      img.removeAttribute('data-wbs-srcset');
    }
  };

  const ecrire = (carte, classe, valeur) => {
    const el = carte.querySelector(classe);
    if (el) el.textContent = valeur || '';
  };

  // Une seule carte dans le DOM : la mettre à jour, c'est y recopier les
  // données de la ligne visée. Le HTML ne porte donc jamais qu'une story par
  // univers, quel qu'en soit le nombre.
  const activer = (rail, index) => {
    const lignes = rail.querySelectorAll('.wb-story-row');
    const carte = rail.querySelector('.wb-story');
    if (!lignes.length || !carte) return;
    const i = ((index % lignes.length) + lignes.length) % lignes.length;
    const row = lignes[i];
    const d = row.dataset;
    const img = carte.querySelector('.wb-story__media');
    const titre = row.querySelector('.wb-story-row__title')?.textContent || '';

    if (img.src) poser(img, d.wbsBig, d.wbsBigset);
    else {
      img.dataset.wbsSrc = d.wbsBig;
      img.dataset.wbsSrcset = d.wbsBigset;
    }
    ecrire(carte, '.wb-story__badge', d.wbTexte ? d.wbBadge : '');
    ecrire(carte, '.wb-story__heading', d.wbTexte ? titre : '');
    ecrire(carte, '.wb-story__cta', d.wbCta);

    // Le lien reste encodé jusqu'à l'interaction : c'est wb-menu-obfuscation
    // qui posera un `href` au pointerdown, et le retirera aussitôt après.
    carte.removeAttribute('href');
    if (d.wbLien) {
      carte.dataset.wbMenuUrl = d.wbLien;
      carte.setAttribute('role', 'link');
      carte.tabIndex = 0;
    } else {
      for (const a of ['data-wb-menu-url', 'role']) carte.removeAttribute(a);
      // Hors de l'ordre de tabulation, mais focalisable : voir plus bas.
      carte.tabIndex = -1;
    }
    if (titre) carte.setAttribute('aria-label', titre);
    else carte.removeAttribute('aria-label');

    // Le thème ferme le mega-menu sur tout `focusout` qui sort du panneau. Une
    // vignette cliquée a le focus et va se masquer en passant à la une : le
    // navigateur le lui retirerait vers rien, et le menu se fermerait au lieu
    // d'afficher la story. Le focus passe d'abord à la carte, qui la montre.
    if (row.contains(document.activeElement)) carte.focus({ preventScroll: true });
    lignes.forEach((ligne, j) => { ligne.hidden = j === i; });
    rail.querySelectorAll('.wb-story__bar').forEach((barre, j) => {
      barre.className = 'wb-story__bar' + (j < i ? ' is-done' : j === i ? ' is-current' : '');
    });
    if (img.src) precharger(lignes, i);
  };

  // UNE image d'avance, et seulement rail ouvert : de quoi éviter la story
  // blanche au changement, sans télécharger tout le rail pour rien.
  const precharger = (lignes, i) => {
    const suivante = lignes[(i + 1) % lignes.length];
    if (suivante) new Image().src = suivante.dataset.wbsBig;
  };

  let panelActif = null;
  const jouer = (panel) => {
    if (panel === panelActif) return;
    const ancien = panelActif && panelActif.querySelector('.wb-stories');
    if (ancien) ancien.classList.remove('is-live');
    panelActif = panel;
    const rail = panel && panel.querySelector('.wb-stories');
    if (!rail) return;
    // Le serveur rend déjà la story 1 : ne rien réécrire dans le cas courant,
    // c'est-à-dire à la toute première ouverture d'un panneau.
    const barres = rail.querySelectorAll('.wb-story__bar');
    if (barres.length && !barres[0].classList.contains('is-current')) activer(rail, 0);
    else precharger(rail.querySelectorAll('.wb-story-row'), 0);
    rail.classList.add('is-live');
  };

  const onEnter = (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    // Survol/focus d'un univers : son panneau devient l'actif.
    const toggle = target.closest('.menu-sidebar__toggle');
    if (toggle) {
      // Le thème masque le panneau sortant au `mouseenter`, qui suit ce
      // `pointerover` : un focus resté dedans (carte, vignette, N2 au clavier)
      // partirait vers rien, et le menu se fermerait. Il passe à l'univers.
      const sortant = document.activeElement?.closest?.('.menu-sidebar__content');
      if (sortant && sortant !== toggle.nextElementSibling) {
        const cible = toggle.querySelector('button[aria-expanded]') || toggle.querySelector('a');
        if (cible) cible.focus({ preventScroll: true });
      }
      reveal(toggle.nextElementSibling);
      jouer(toggle.nextElementSibling);
      return;
    }
    // Entrée dans le mega-menu : seul le panneau déjà actif sera visible.
    if (!target.closest('nav-dropdown-mega')) return;
    const active = document.querySelector('.menu-sidebar__toggle.is-visible');
    if (active) {
      reveal(active.nextElementSibling);
      jouer(active.nextElementSibling);
    }
  };

  const onClick = (event) => {
    const row = event.target.closest?.('[data-wb-go]');
    if (!row) return;
    event.preventDefault();
    activer(row.closest('.wb-stories'), Number(row.dataset.wbGo));
  };

  const onKey = (event) => {
    if (event.key === 'Escape') return jouer(null);
    const row = event.target.closest?.('[data-wb-go]');
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    activer(row.closest('.wb-stories'), Number(row.dataset.wbGo));
  };

  const onEnd = (event) => {
    if (event.animationName !== 'wb-story-fill') return;
    const rail = event.target.closest('.wb-stories');
    if (!rail) return;
    const barres = [...rail.querySelectorAll('.wb-story__bar')];
    activer(rail, barres.indexOf(event.target) + 1);
  };

  // Écoute portée par la navigation seule : `pointerover` sur `document`
  // se déclencherait à chaque survol de la page entière.
  const install = () => {
    const nav = document.getElementById('MainNavigation');
    if (!nav) return false;
    for (const type of ['pointerover', 'focusin']) nav.addEventListener(type, onEnter);
    nav.addEventListener('click', onClick);
    nav.addEventListener('keydown', onKey);
    nav.addEventListener('animationend', onEnd);
    // Sortie du menu : plus rien ne tourne, et la prochaine ouverture repart
    // de la première story.
    nav.addEventListener('pointerleave', () => jouer(null));
    return true;
  };
  if (!install()) document.addEventListener('DOMContentLoaded', install, { once: true });
})();
